import { memo } from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import { NakedButton, styles, Text, useTooltip } from '@frontend/design-system';
import { iconButtonStyles, navItemStyles } from '../../styles/styles';
import { TopBarFocusIds } from '../../utils/focus-registry';

type WorkspaceNavButtonProps = {
  isSelected: boolean;
  disabled: boolean;
  onClick: (e: any) => void;
  showLabel: boolean;
  isMobile: boolean;
  workspaceName: string;
  locationCount: number;
  isSelectedLocation?: boolean;
};

export const WorkspaceNavButton = memo(
  ({ isSelected, disabled, onClick, showLabel, isMobile, workspaceName, locationCount }: WorkspaceNavButtonProps) => {
    const { t } = useTranslation();
    const { Tooltip, tooltipProps, triggerProps } = useTooltip({ placement: 'bottom' });

    return (
      <NakedButton
        {...triggerProps}
        css={[
          iconButtonStyles({ isSelected, isMobile }),
          disabled &&
            css`
              pointer-events: none;
              :hover {
                background: inherit;
              }
            `,
        ]}
        onClick={onClick}
        id={TopBarFocusIds.location}
        data-test-id='nav-workspace-button'
        trackingId='nav-workspace-button'
      >
        <Icon name='bookmark' size={24} />
        {showLabel && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              align-items: flex-start;
            `}
          >
            <Text css={navItemStyles({ isXsmall: false, isMobile, isSelected, truncate: styles.truncate })}>
              {workspaceName}
            </Text>
            <Text
              size='small'
              color='subdued'
              css={navItemStyles({ isXsmall: false, isMobile, isSelected, truncate: styles.truncate })}
            >
              {locationCount} {locationCount === 1 ? t('location') : t('locations')}
            </Text>
          </div>
        )}
        <Tooltip
          {...tooltipProps}
          css={css`
            text-align: center;
            display: flex;
            flex-direction: column;
            gap: ${theme.spacing(0.25)};
          `}
        >
          <Text size='small' color='white' weight='bold'>
            {t('Workspace mode is active')}
          </Text>
          <Text
            size='small'
            color='white'
            css={css`
              opacity: 0.85;
            `}
          >
            {t('To exit, manually choose and apply a location from the location picker.')}
          </Text>
        </Tooltip>
      </NakedButton>
    );
  }
);
WorkspaceNavButton.displayName = 'WorkspaceNavButton';
